import clsx from 'clsx';
import { Button } from '@/components/button';
import styles from './pagination.module.css';
import { MaterialSymbolIcon } from '@/components/material-symbol-link';
import { RadioGroup } from './inputs/radio-group';
import { MAX_PAGES_COUNT, PAGE_SIZE_OPTIONS } from '@/util/constants';
import { DropdownInput } from './inputs/dropdown-input';
import { TranslationKey } from '@/contexts/translations';
import { useTranslation } from '@/contexts/i18n-context';

interface PaginationProps {
  pageSize: number;
  handlePageSizeChange: (val: number) => void;
  pageSizeTxt: TranslationKey;
  pages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  label: string;
  readersOnlyLabel?: boolean;
}

export function Pagination({
  label,
  readersOnlyLabel,
  pages,
  currentPage,
  onPageChange,
  pageSize,
  handlePageSizeChange,
  pageSizeTxt,
}: PaginationProps) {
  const { t } = useTranslation();
  const isSelected = (page: number) => page === currentPage;

  // Number of pages to display in the pagination (MAX_PAGES_COUNT or less if total pages < MAX_PAGES_COUNT)
  const pagesCountToDisplay = Math.min(pages, MAX_PAGES_COUNT);

  // The minimum page number that should be displayed in the current pagination state
  const leftBoundary = Math.max(1, currentPage - Math.floor(MAX_PAGES_COUNT / 2));

  // The actual first page to display, accounting for edge case where we're near the end
  // as there is always a maximum page that can be shown first in the list, which can be
  // calculated by this formula -> [(total pages) - (pages which are shown) + 1]
  const firstPageInList = Math.min(leftBoundary, pages - pagesCountToDisplay + 1);

  return (
    <div className={styles.pagination}>
      <div className={styles.pageSizeGroup}>
        <DropdownInput text={pageSize}>
          <PageSizeRadioGroup
            handlePageSizeChange={handlePageSizeChange}
            pageSize={pageSize}
            label={label}
            readersOnlyLabel={readersOnlyLabel}
          />
        </DropdownInput>
        <span>{t(pageSizeTxt)}</span>
      </div>
      <div className={styles.pages}>
        {currentPage > 1 && (
          <Button
            variant="textOnly"
            className={styles.paginationArrow}
            onClick={() => onPageChange(currentPage - 1)}
          >
            <MaterialSymbolIcon name="chevron_left" />
          </Button>
        )}
        {Array.from({ length: pagesCountToDisplay }, (_, i) => i + firstPageInList).map((page) => (
          <Button
            key={page}
            variant={isSelected(page) ? 'filled' : 'textOnly'}
            className={clsx(styles.paginationButton)}
            onClick={() => onPageChange(page)}
          >
            {page}
          </Button>
        ))}
        {currentPage < pages && (
          <Button
            variant="textOnly"
            className={styles.paginationArrow}
            onClick={() => onPageChange(currentPage + 1)}
          >
            <MaterialSymbolIcon name="chevron_right" />
          </Button>
        )}
      </div>
    </div>
  );
}

interface PageSizeRadioGroup {
  pageSize: number;
  handlePageSizeChange: (val: number) => void;
  label: string;
  readersOnlyLabel?: boolean;
}

function PageSizeRadioGroup({
  handlePageSizeChange,
  pageSize,
  label,
  readersOnlyLabel,
}: PageSizeRadioGroup) {
  return (
    <RadioGroup
      className={styles.pageSizeOptions}
      label={label}
      readersOnlyLabel={readersOnlyLabel}
      variant="dropdown"
      name="pageSize"
      defaultCheckedValue={pageSize.toString()}
      onChange={(e) => handlePageSizeChange(Number(e.target.value))}
      options={PAGE_SIZE_OPTIONS.map((pageSize) => ({
        value: pageSize,
        label: pageSize,
      }))}
    />
  );
}
